import { useCallback, useMemo, useState, type MouseEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowUpRight, Eye, ShoppingBag, Star, Zap, Calendar, Globe } from 'lucide-react'
import type { Product } from '@/services/products'
import type { ProductBadge } from '@/storefront/lib/shopBadges'
import { getShopProductBadge, isShopProductOutOfStock } from '@/storefront/lib/shopBadges'
import { listProductDisplayPrice } from '../lib/listPrice'
import { formatTnd } from '../lib/formatMoney'
import { useTheme } from '@/storefront/theme/ThemeContext'
import { useQuickView } from '@/storefront/context/QuickViewContext'
import { useOptionalReferenceStore } from '@/storefront/context/ReferenceStoreContext'
import { cn } from '@/lib/utils'
import type { ProductCardVariant } from '@/storefront/theme/uiTokens'
import { normalizeStorefrontProduct, type NormalizedProduct } from '@/core/product'
import { resolveVariant } from '@/core/variant'
import { addToCart } from '@/core/cart'
import { catalogProductToRawStorefront } from '@/storefront/lib/catalogProductToRaw'
import { useInViewAnimation } from '@/storefront/hooks/useInViewAnimation'
import { useStorefrontEditorSurface } from '@/storefront/hooks/useStorefrontEditorSurface'
import { catalogProductNeedsVariantChoice } from '@/storefront/lib/variantChoice'
import { computeDiscount } from '@/lib/compareAtPrice'
import { detectColorOption, isLightFillHex, normalizeColorCached, normalizeTrustedHexInput } from '@/lib/colorVariants'
import { useSecondaryProductImageUrl, useStorefrontProductImage } from '@/storefront/hooks/useStorefrontProductImage'

function pickSwatchDisplayValue(attrs: Record<string, string>): string {
  const entries = Object.entries(attrs).filter(([k]) => !k.startsWith('_'))
  for (const [k, v] of entries) {
    if (detectColorOption(k)) {
      const s = String(v ?? '').trim()
      if (s) return s
    }
  }
  const vals = entries.map(([, v]) => String(v ?? '').trim()).filter(Boolean)
  return vals[0] || ''
}

function buildVariantSwatchPreview(product: Product): { id: string; image?: string; color?: string; label: string }[] {
  const rows = product.variant_swatches ?? []
  const out: { id: string; image?: string; color?: string; label: string }[] = []
  const seen = new Set<string>()
  for (const r of rows) {
    const attrs = r.attributes || {}
    const displayVal = pickSwatchDisplayValue(attrs as Record<string, string>)
    const key = displayVal || r.id
    if (seen.has(key)) continue
    seen.add(key)
    const image = (r.image_url || '').trim() || undefined
    const rowHex = normalizeTrustedHexInput(String(r.display_hex ?? ''))
    const meta = displayVal ? normalizeColorCached(displayVal, rowHex ? { trustedHex: rowHex } : undefined) : null
    const color = meta?.hex ?? undefined
    if (!image && !displayVal) continue
    out.push({
      id: r.id,
      image,
      color,
      label: displayVal || key,
    })
    if (out.length >= 6) break
  }
  return out
}

const BADGE_CONFIG: Record<ProductBadge, { label: string; className: string; icon?: React.ReactNode }> = {
  digital: { label: 'Numérique', className: 'bg-primary/15/90 text-white', icon: <Zap className="h-2.5 w-2.5" /> },
  service: { label: 'Service', className: 'bg-blue-600/90 text-white', icon: <Calendar className="h-2.5 w-2.5" /> },
  dropship: { label: 'Import', className: 'bg-amber-500/90 text-white', icon: <Globe className="h-2.5 w-2.5" /> },
  sale: { label: 'Promo', className: 'bg-red-600/90 text-white' },
  new: { label: 'Nouveau', className: 'bg-emerald-500/15 text-emerald-950 ring-1 ring-emerald-900/15' },
  hot: { label: 'Populaire', className: 'bg-amber-500/15 text-amber-950 ring-1 ring-amber-900/15' },
}

function canLineItemAdd(normalized: NormalizedProduct, resolved: ReturnType<typeof resolveVariant>): boolean {
  if (!resolved.ok) return false
  return (
    normalized.isDropshipping ||
    !normalized.trackInventory ||
    (normalized.hasVariants && resolved.variant
      ? resolved.variant.status === 'active' &&
        (resolved.variant.inventoryQuantity ?? resolved.variant.stock ?? 0) > 0
      : normalized.stock > 0 && normalized.status !== 'out_of_stock')
  )
}

export function ProductCard({
  product,
  to,
  badge,
  variant: variantProp,
  enableQuickAdd,
  revealOnScroll,
  showPrice,
  showRating,
  merchBadges = true,
  imageHover = true,
  showVariantSwatches = false,
  showStockIndicator = false,
  cardLayout = 'grid',
}: {
  product: Product
  to: string
  badge?: ProductBadge | null
  /** Per-card override; defaults to theme.ui.productCard */
  variant?: ProductCardVariant
  /** When undefined, falls back to `theme.ui.productCardQuickAdd`. */
  enableQuickAdd?: boolean
  /** Optional scroll-driven fade-up for the whole card. */
  revealOnScroll?: boolean
  /** When false, hides the price row (compare-at still hidden with price). */
  showPrice?: boolean
  /** When true, shows a compact popularity line (order count). */
  showRating?: boolean
  /** When false, hides sale/new/hot/digital strip (manual badge still applies if passed). */
  merchBadges?: boolean
  /** When false, disables second-image hover swap (still zooms if single image). */
  imageHover?: boolean
  /** When true, shows variant preview dots from `product.variant_swatches`. */
  showVariantSwatches?: boolean
  /** When true, shows in-stock / low / out copy under the price. */
  showStockIndicator?: boolean
  /** `list` = horizontal card for dense shop rows. */
  cardLayout?: 'grid' | 'list'
}) {
  const theme = useTheme()
  const variant = variantProp ?? theme.ui.productCard
  const quickView = useQuickView()
  const refStore = useOptionalReferenceStore()
  const isEditorSurface = useStorefrontEditorSurface()
  const navigate = useNavigate()
  const motion = useInViewAnimation({
    disabled: revealOnScroll !== true || theme.ui.blockScrollMotion === false,
    staggerMs: theme.ui.motionLevel === 'premium' ? 56 : 40,
    staggerIndex: 0,
  })

  const quickAddOn =
    enableQuickAdd !== undefined ? enableQuickAdd === true : theme.ui.productCardQuickAdd === true
  const showPriceRow = showPrice !== false

  const { displayUrl: primarySrc } = useStorefrontProductImage(product)
  const secondarySrc = useSecondaryProductImageUrl(product, primarySrc)

  const [mediaHover, setMediaHover] = useState(false)
  const showSecond = imageHover && Boolean(secondarySrc) && mediaHover
  const swatches = useMemo(() => {
    if (!showVariantSwatches || !catalogProductNeedsVariantChoice(product)) return []
    return buildVariantSwatchPreview(product)
  }, [product, showVariantSwatches])
  const outOfStock = isShopProductOutOfStock(product)
  const primaryBadge = merchBadges ? badge ?? getShopProductBadge(product) : badge ?? null
  const totalStock = product.total_stock ?? product.stock
  const lowStock =
    !product.is_dropshipping &&
    product.track_inventory !== false &&
    typeof totalStock === 'number' &&
    totalStock > 0 &&
    totalStock <= 5

  const normalized = useMemo(
    () => normalizeStorefrontProduct(catalogProductToRawStorefront(product)),
    [product]
  )
  const resolvedDefault = useMemo(() => resolveVariant(normalized, {}), [normalized])
  const showQuickAddBar =
    quickAddOn &&
    refStore &&
    !outOfStock &&
    (!product.has_variants ? canLineItemAdd(normalized, resolvedDefault) : true)

  const priceLabel = listProductDisplayPrice(product)
  const salePrice = !product.has_variants ? product.price : null
  const compareAt =
    !product.has_variants && product.compare_at_price != null ? Number(product.compare_at_price) : null
  const hasDiscount = compareAt != null && salePrice != null && compareAt > salePrice
  const discountPct =
    hasDiscount && salePrice != null && compareAt != null
      ? computeDiscount(Number(salePrice), compareAt)?.pct ?? null
      : null

  const showQuickView = quickView?.enabled === true
  const [adding, setAdding] = useState(false)

  const handleQuickAdd = useCallback(
    (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (!refStore || !quickAddOn) return
      if (!product.has_variants) {
        if (!canLineItemAdd(normalized, resolvedDefault)) {
          navigate(to)
          return
        }
        setAdding(true)
        const r = addToCart(refStore.cart, normalized, null, null, 1)
        setAdding(false)
        if (!r.ok) {
          navigate(to)
          return
        }
        refStore.setCart(r.cart)
        refStore.openCartDrawer()
        return
      }
      const hasVariantRows =
        (product.variants?.length ?? 0) > 0 || (product.variant_swatches?.length ?? 0) > 0
      if (!hasVariantRows || !resolvedDefault.ok || !canLineItemAdd(normalized, resolvedDefault)) {
        navigate(to)
        return
      }
      setAdding(true)
      const r = addToCart(
        refStore.cart,
        normalized,
        resolvedDefault.variant,
        resolvedDefault.variantId,
        1
      )
      setAdding(false)
      if (!r.ok) {
        navigate(to)
        return
      }
      refStore.setCart(r.cart)
      refStore.openCartDrawer()
    },
    [
      refStore,
      quickAddOn,
      product.has_variants,
      product.variants?.length,
      product.variant_swatches?.length,
      normalized,
      resolvedDefault,
      navigate,
      to,
    ]
  )

  const isPremiumMotion = theme.ui.motionLevel === 'premium'

  const shell = cn(
    'sf-product-card-root group relative overflow-hidden transition ease-out duration-sf-slow',
    cardLayout === 'list' && 'flex flex-col sm:flex-row sm:items-stretch',
    variant === 'default' &&
      cn(
        `rounded-ref-xl ring-1 ring-border/80 ${theme.components.card}`,
        isPremiumMotion
          ? 'hover:-translate-y-2.5 hover:shadow-[0_28px_60px_-24px_rgb(0_0_0/0.26)] hover:ring-border/25'
          : 'hover:-translate-y-1.5 hover:shadow-ref-card hover:ring-border/20'
      ),
    variant === 'minimal' &&
      cn(
        'rounded-md border border-border/90 bg-ref-elevated shadow-none hover:border-border',
        isPremiumMotion
          ? 'hover:-translate-y-1 hover:shadow-lg'
          : 'hover:-translate-y-0.5 hover:shadow-md'
      ),
    variant === 'luxury' &&
      cn(
        'rounded-ref-lg border border-border/60 bg-ref-elevated shadow-[0_1px_0_rgb(0_0_0/0.04)] ring-1 ring-amber-900/10',
        isPremiumMotion
          ? 'hover:-translate-y-2 hover:shadow-[0_32px_70px_-30px_rgb(0_0_0/0.28)]'
          : 'hover:-translate-y-1 hover:shadow-[0_24px_55px_-28px_rgb(0_0_0/0.22)]'
      ),
    variant === 'bold' &&
      cn(
        'rounded-ref-md border-2 border-ref-primary bg-ref-elevated shadow-md',
        isPremiumMotion
          ? 'hover:-translate-y-2 hover:scale-[1.02] hover:shadow-2xl'
          : 'hover:-translate-y-1.5 hover:scale-[1.01] hover:shadow-xl'
      )
  )

  const mediaAspect = cn(
    cardLayout === 'list' && 'aspect-square w-full sm:aspect-square sm:max-w-[168px]',
    cardLayout !== 'list' && (variant === 'minimal' ? 'aspect-square' : variant === 'luxury' ? 'aspect-[3/4]' : 'aspect-[4/5]')
  )

  const titleCls = cn(
    'line-clamp-2 leading-snug tracking-tight text-ref-text',
    variant === 'default' && 'text-[0.9375rem] font-semibold',
    variant === 'minimal' && 'text-sm font-medium',
    variant === 'luxury' && "font-serif text-[1.02rem] font-semibold tracking-tight",
    variant === 'bold' && 'text-base font-extrabold uppercase tracking-wide'
  )

  const priceCls = cn(
    'tabular-nums tracking-tight text-ref-text',
    variant === 'luxury' && 'text-[1.05rem] font-semibold',
    variant === 'bold' && 'text-lg font-black',
    (variant === 'default' || variant === 'minimal') && 'text-[0.95rem] font-bold'
  )

  const bodyPad = variant === 'minimal' ? 'p-3 sm:p-4' : variant === 'luxury' ? 'p-5 sm:p-6' : 'p-4 sm:p-5'

  const zoomHover =
    !showSecond &&
    cn(
      'sf-product-card-media h-full w-full object-cover transition ease-out will-change-transform duration-sf-slow',
      variant === 'minimal' && (isPremiumMotion ? 'group-hover:scale-[1.08]' : 'group-hover:scale-[1.04]'),
      variant === 'luxury' && (isPremiumMotion ? 'group-hover:scale-[1.1]' : 'group-hover:scale-[1.05]'),
      variant === 'bold' && (isPremiumMotion ? 'group-hover:scale-[1.1]' : 'group-hover:scale-[1.06]'),
      variant === 'default' && (isPremiumMotion ? 'group-hover:scale-[1.1]' : 'group-hover:scale-[1.07]')
    )

  const stockLine = (() => {
    if (!showStockIndicator) return null
    if (product.is_dropshipping) return null
    if (product.track_inventory === false) return null
    if (outOfStock) {
      return <p className="mt-1.5 text-xs font-semibold text-red-700">Rupture de stock</p>
    }
    if (lowStock) {
      return (
        <p className="mt-1.5 text-xs font-semibold text-amber-800">
          Plus que {totalStock} en stock
        </p>
      )
    }
    return <p className="mt-1.5 text-xs font-medium text-emerald-800/90">En stock</p>
  })()

  const inner = (
    <div className={shell}>
      <div className={cn('relative', cardLayout === 'list' && 'sm:w-[168px] shrink-0')}>
        <Link to={to} className="block" aria-label={product.name}>
          <div
            className={cn('relative overflow-hidden bg-ref-elevated', mediaAspect)}
            onMouseEnter={() => setMediaHover(true)}
            onMouseLeave={() => setMediaHover(false)}
          >
            <div className="absolute left-3 top-3 z-10 flex flex-col gap-1">
              {primaryBadge ? (
                <span
                  className={cn(
                    'inline-flex items-center gap-1 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] shadow-sm backdrop-blur-sm',
                    variant === 'bold' ? 'rounded-sm' : 'rounded-full',
                    BADGE_CONFIG[primaryBadge]?.className ?? ''
                  )}
                >
                  {BADGE_CONFIG[primaryBadge]?.icon}
                  {BADGE_CONFIG[primaryBadge]?.label ?? primaryBadge}
                </span>
              ) : null}
              {outOfStock ? (
                <span className="inline-flex items-center rounded-full bg-foreground px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white shadow-sm">
                  Rupture de stock
                </span>
              ) : null}
              {primaryBadge !== 'sale' && hasDiscount && discountPct ? (
                <span className="inline-flex items-center rounded-full bg-red-600 px-2.5 py-0.5 text-[10px] font-bold text-white shadow-sm">
                  -{discountPct}%
                </span>
              ) : null}
            </div>
            {primarySrc ? (
              <>
                <img
                  src={primarySrc}
                  alt=""
                  className={cn(
                    'sf-product-card-media absolute inset-0 h-full w-full object-cover transition-opacity ease-out duration-sf-slow',
                    showSecond ? 'opacity-0' : 'opacity-100',
                    !showSecond ? zoomHover : 'scale-100'
                  )}
                  loading="lazy"
                />
                {secondarySrc ? (
                  <img
                    src={secondarySrc}
                    alt=""
                    className={cn(
                      'sf-product-card-media absolute inset-0 h-full w-full object-cover transition-opacity ease-out duration-sf-slow',
                      showSecond ? 'opacity-100' : 'opacity-0'
                    )}
                    loading="lazy"
                  />
                ) : null}
              </>
            ) : isEditorSurface ? (
              <div className="flex h-full items-center justify-center px-2 text-center text-[11px] font-medium text-amber-900/90">
                Pas d’image — ajoutez-en dans le catalogue
              </div>
            ) : (
              <div className="h-full w-full bg-muted/35" aria-hidden />
            )}
            {variant !== 'minimal' ? (
              <>
                <div
                  className={cn(
                    'pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/55 via-black/20 to-transparent transition-opacity duration-500',
                    variant === 'default' ? 'h-[52%]' : 'h-2/5',
                    'opacity-0 group-hover:opacity-100'
                  )}
                  aria-hidden
                />
                <span
                  className={cn(
                    'pointer-events-none absolute bottom-14 right-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-card/95 text-foreground opacity-0 shadow-xl backdrop-blur-sm transition duration-500 group-hover:translate-y-0 group-hover:opacity-100 sm:bottom-16',
                    'translate-y-1'
                  )}
                  aria-hidden
                >
                  <ArrowUpRight className="h-4 w-4" />
                </span>
              </>
            ) : null}
            {showQuickAddBar ? (
              <div
                className={cn(
                  'pointer-events-auto absolute inset-x-0 bottom-0 z-20 flex justify-center px-3 pb-3 transition duration-500 ease-out',
                  'translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100'
                )}
              >
                <button
                  type="button"
                  disabled={adding || (!product.has_variants && !canLineItemAdd(normalized, resolvedDefault))}
                  onClick={handleQuickAdd}
                  className={cn(
                    'inline-flex min-h-[2.5rem] items-center justify-center gap-2 rounded-full px-4 text-[11px] font-bold uppercase tracking-wide shadow-lg ring-1 backdrop-blur-md transition duration-300',
                    'bg-ref-primary text-white ring-border/10 hover:brightness-110 active:scale-95',
                    'disabled:pointer-events-none disabled:opacity-40'
                  )}
                >
                  <ShoppingBag className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  {adding
                    ? 'Ajout…'
                    : product.has_variants &&
                        (product.variants?.length ?? 0) === 0 &&
                        (product.variant_swatches?.length ?? 0) === 0
                      ? 'Choisir les options'
                      : product.has_variants && !resolvedDefault.ok
                        ? 'Choisir les options'
                        : product.has_variants
                          ? 'Ajout rapide'
                          : 'Ajouter au panier'}
                </button>
              </div>
            ) : null}
          </div>
        </Link>
        {showQuickView ? (
          <button
            type="button"
            className={cn(
              'absolute z-20 flex items-center gap-1.5 rounded-full bg-card/95 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-foreground shadow-lg ring-1 ring-border/80 backdrop-blur-sm transition hover:bg-card',
              quickAddOn ? 'right-3 top-3' : 'bottom-3 left-3'
            )}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              quickView?.open(product)
            }}
          >
            <Eye className="h-3.5 w-3.5" aria-hidden />
            Aperçu rapide
          </button>
        ) : null}
      </div>
      <Link to={to} className={cn('block min-w-0 flex-1', bodyPad, cardLayout === 'list' && 'flex flex-col justify-center')}>
        <h3 className={titleCls}>{product.name}</h3>
        {showRating === true ? (
          <p className="mt-1.5 flex items-center gap-1 text-[11px] font-semibold text-amber-800 [.sf-ui-mode-dark_&]:text-amber-200/90">
            <Star className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-400" aria-hidden />
            {(product.orders_count ?? 0) > 0 ? (
              <span>{(product.orders_count ?? 0).toLocaleString()} orders</span>
            ) : (
              <span className="font-medium text-ref-secondary">Nouveau</span>
            )}
          </p>
        ) : null}
        {showPriceRow ? (
          <div className="mt-2 flex flex-wrap items-baseline gap-2">
            <p className={priceCls}>{priceLabel}</p>
            {hasDiscount && compareAt != null ? (
              <p className="text-xs font-medium tabular-nums text-ref-muted line-through">{formatTnd(compareAt)}</p>
            ) : null}
          </div>
        ) : null}
        {stockLine}
        {swatches.length > 0 ? (
          <div className="mt-3 flex flex-wrap items-center gap-1.5" aria-label="Options de variante">
            {swatches.map((s) => (
              <span
                key={s.id}
                title={s.label || undefined}
                className={cn(
                  'inline-flex shrink-0 items-center justify-center overflow-hidden border border-ref-border bg-ref-elevated shadow-sm ring-1 ring-ref-border/80',
                  s.image || s.color
                    ? 'h-6 w-6 rounded-full text-[10px] font-bold text-ref-secondary'
                    : 'min-h-6 max-w-[5.5rem] rounded-md px-1 py-0.5 text-[9px] font-semibold leading-tight text-ref-text',
                )}
              >
                {s.image ? (
                  <img src={s.image} alt="" className="h-full w-full object-cover" loading="lazy" />
                ) : s.color ? (
                  <span
                    className={cn(
                      'h-5 w-5 rounded-full',
                      isLightFillHex(s.color) ? 'border border-border' : 'border border-black/15',
                    )}
                    style={{ background: s.color }}
                  />
                ) : (
                  <span className="line-clamp-2 break-words text-center">{s.label}</span>
                )}
              </span>
            ))}
          </div>
        ) : null}
        <p
          className={cn(
            'mt-3 flex translate-y-0.5 items-center gap-1 text-xs font-semibold text-amber-800 transition duration-300 [.sf-ui-mode-dark_&]:text-amber-200/90',
            variant === 'minimal' ? 'opacity-100' : 'opacity-0 group-hover:translate-y-0 group-hover:opacity-100'
          )}
        >
          Voir le produit
          <ArrowUpRight className="h-3 w-3" aria-hidden />
        </p>
      </Link>
    </div>
  )

  if (revealOnScroll === true) {
    return (
      <div ref={motion.ref} className={motion.className} style={motion.style}>
        {inner}
      </div>
    )
  }

  return inner
}
